import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { ReduxEntitySelect, ReduxInput, ReduxSwitch } from 'components/FormElements'
// constants
import { Forms } from 'constants/forms'
// graphql
import {
  RISK_VIEWS_SELECT_QUERY,
  RISK_TEMPLATE_SELECT_QUERY,
  RISK_VIEW_FILTERS_SELECT_QUERY
} from 'graphql/risk/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { IRiskView, IFilter, FilterTarget, IRiskTemplate } from 'types/graphql'
// utils
import { safeGetOr } from 'utils/fp'
import { map, pipe } from 'lodash/fp'

const selector = formValueSelector(Forms.DASHBOARD_WIDGET_SETTINGS_FORM)

const getTemplatesOptions = pipe(
  safeGetOr<IRiskTemplate, any, IRiskTemplate[]>([], 'data.riskTemplates.data'),
  map<IRiskTemplate, IOption>(({ id, name, archived }) => ({ label: name, value: id, isDisabled: archived }))
)

const getRiskViewsOptions = pipe(
  safeGetOr<IRiskView, any, IRiskView[]>([], 'data.riskViews.data'),
  map<IRiskView, IOption>(({ id, name }) => ({ label: name || '', value: id }))
)

const getRiskViewFiltersOptions = pipe(
  safeGetOr<IFilter, any, IFilter[]>([], 'data.filters.data'),
  map<IFilter, IOption>(({ id, name }) => ({ label: name || '', value: id }))
)

const CardFields: React.FC = () => {
  const dispatch = useDispatch()

  const riskView = useSelector((state) => selector(state, 'riskViewId'))
  const riskTemplate = useSelector((state) => selector(state, 'riskTemplateId'))

  const onTargetChange = () => {
    dispatch(change(Forms.DASHBOARD_WIDGET_SETTINGS_FORM, 'riskViewId', null))
    dispatch(change(Forms.DASHBOARD_WIDGET_SETTINGS_FORM, 'filterId', null))
  }

  return (
    <>
      <ReduxInput label='Title' name='title' />
      <ReduxEntitySelect
        label='Target'
        name='riskTemplateId'
        getOptions={getTemplatesOptions}
        query={RISK_TEMPLATE_SELECT_QUERY}
        onSelectChange={onTargetChange}
        handleSearch={({ search, refetch }) => refetch({ filter: { name: search } })}
      />
      <ReduxEntitySelect
        key={`riskView-${riskTemplate?.value}`}
        name='riskViewId'
        label='Risk View'
        skip={!riskTemplate}
        isDisabled={!riskTemplate}
        query={RISK_VIEWS_SELECT_QUERY}
        getOptions={getRiskViewsOptions}
        variables={{
          filter: {
            riskTemplateIds: [riskTemplate?.value]
          }
        }}
      />
      <ReduxEntitySelect
        key={`filter-${riskTemplate?.value}`}
        label='Filter'
        name='filterId'
        skip={!riskTemplate}
        isDisabled={!riskView}
        getOptions={getRiskViewFiltersOptions}
        query={RISK_VIEW_FILTERS_SELECT_QUERY}
        variables={{
          filter: {
            targetId: riskTemplate?.value,
            target: FilterTarget.RiskTemplate
          }
        }}
      />
      <ReduxSwitch label='Open in new window' name='openInWindow' />
      <ReduxInput name='width' label='Width (%)' type='number' />
      <ReduxInput name='height' label='Height (px)' type='number' />
    </>
  )
}

export default CardFields
